import moment from 'moment';

import {Issue, Machine, MachineTable, Service} from '~/types';
import store from '~/store';
import { DatabaseClient, getDatabaseClient } from '~/data/db/DatabaseClient';
import { resolveOfflineDb } from './offlineDatabase';
import QueryBuilder from "./queryBuilder";
import FileTransfer from "./fileTransfer";
import {IssueStatuses, IssueTypes} from "~/app/data/statuses/issue";
import { syncTypes } from '~/sqlite/syncDataTypes';
import { syncQueue } from '~/data/sync/SyncQueue';
import { notifyPeriodicCheckQueueChanged } from '~/data/sync/periodicCheckQueue';
import { OperatingTypes } from '~/app/data/statuses/operatingData';
import { OperatingTimePayload } from '~/machines/store/machine';
import { getLastUsedMachineIds, isLastUsedMachine, hasPendingSyncForMachine } from '~/sqlite/lastUsedCache';
import { diffByRowDataEquality } from '~/data/local/diffHelper';

export class MachineOfflineService {

    constructor(private readonly databaseClient: DatabaseClient | null = getDatabaseClient()) {}

    private readonly qb: QueryBuilder = new QueryBuilder();
    private get SQLite() {
        return resolveOfflineDb(this.databaseClient);
    }

    public reset(machines: Machine[]) {
        const db = this.SQLite;
        if (!db) {
            return Promise.resolve();
        }
        return new Promise((resolve, reject) => {
            this.downloadMachinesImages(machines).then(async ({machines: machineRows, deleteMachines}) => {
                const fileTransfer = new FileTransfer();
                const safeDeleteMachines: string[] = [];
                for (const mId of deleteMachines) {
                    const hasPending = await hasPendingSyncForMachine(mId);
                    if (!hasPending) {
                        safeDeleteMachines.push(mId);
                    }
                }
                db.transaction((tx) => {
                    if (safeDeleteMachines.length) {
                        const deleteMachine = this.qb.deleteIn('Machine', 'id', safeDeleteMachines);
                        const deleteDocuments = this.qb.deleteIn('Documents', 'machineId', safeDeleteMachines);
                        const deleteIssues = this.qb.deleteIn('Issue', 'machineId', safeDeleteMachines);
                        const deleteServices = this.qb.deleteIn('Service', 'machineId', safeDeleteMachines);
                        const deleteSpecs = this.qb.deleteIn('MachineSpecification', 'id', safeDeleteMachines);
                        tx.executeSql(deleteMachine.query, deleteMachine.params);
                        tx.executeSql(deleteDocuments.query, deleteDocuments.params);
                        tx.executeSql(deleteIssues.query, deleteIssues.params);
                        tx.executeSql(deleteServices.query, deleteServices.params);
                        tx.executeSql(deleteSpecs.query, deleteSpecs.params);
                        safeDeleteMachines.forEach((machine) => {
                            fileTransfer.removeFolder(`issueImages/${machine}`);
                            fileTransfer.removeFolder(`archives/${machine}`);
                        });
                    }
                    if (machineRows && machineRows.length) {
                        const insert = this.qb.insertOrReplace('Machine', machineRows);
                        tx.executeSql(insert.query, insert.params);
                    }
                }, (e) => reject(e), () => resolve(undefined));
            }, (e) => reject(e));
        });
    }

    public downloadMachinesImages(machinesWithImage: Machine[]): Promise<{machines: MachineTable[], deleteMachines: string[]}> {
        const db = this.SQLite;
        if (!db) {
            return Promise.resolve({ machines: [], deleteMachines: [] });
        }

        const allPromise: Promise<MachineTable>[] = [];
        return new Promise<{machines: MachineTable[], deleteMachines: string[]}>((resolve, reject) => {
            const { query, params } = this.qb.select('Machine');
            db.executeSql(query, params, (resultSet) => {
                const machines = machinesWithImage.map((data) => ({
                    data: JSON.stringify(data),
                    id: data.id,
                    categoryImage: data.categoryImage,
                }));
                const dbMachines = this.qb.getAllResult(resultSet);
                const protectedIds = new Set(getLastUsedMachineIds());
                const { toAdd: addMachines, toDeleteIds } = diffByRowDataEquality(dbMachines, machines);
                const deleteMachines = toDeleteIds.filter((id) => !protectedIds.has(String(id)));

                addMachines.forEach((machine) => {
                    allPromise.push(this.downloadMachineImages(machine));
                });

                Promise.all(allPromise).then(downloaded => {
                    resolve({ machines: downloaded, deleteMachines });
                });
            }, (e) => reject(e));
        });
    }

    public downloadMachineImages(machine: MachineTable) {
        return new Promise<MachineTable>((resolve) => {
            if (machine.categoryImage) {
                const fileTransfer = new FileTransfer();
                fileTransfer.downloadFileFromURL(machine.categoryImage, 'categoryImages')
                    .then((image) => resolve({...machine, categoryImage: image}));
            } else {
                resolve(machine);
            }
        });
    }

    public getAll(): Promise<MachineTable[] | undefined> {
        const db = this.SQLite;
        if (!db) return Promise.resolve(undefined);
        const { query, params } = this.qb.select('Machine');
        return new Promise<MachineTable[]>((resolve, reject) => {
            db.executeSql(query, params, (resultSet) => resolve(this.qb.getAllResult(resultSet)), (e) => reject(e));
        });
    }

    public machineDetails(id: string): Promise<Machine | undefined> {
        const db = this.SQLite;
        if (!db) return Promise.resolve(undefined);
        const issues: Machine['issues'] = [];
        const services: Machine['services'] = [];
        const issuesStatusCount = {};
        let issuesCount: Machine['issuesCount'] = [];
        let excavatorData;
        const where = [{name: 'id', value: id}];
        const innerWhere = [{name: 'machineId', value: id}];
        const issuesOrderBy = [{name: 'id', value: 'DESC'}];
        return new Promise<Machine>((resolve, reject) => {
            db.transaction((tx) => {
                const machineQuery = new Promise<void>((resolveMachine, rejectMachine) => {
                    const { query, params } = this.qb.select('Machine', [], where);
                    tx.executeSql(query, params, (_tx, result) => {
                        if (result && result.rows.length > 0) {
                            excavatorData = this.qb.getSingleResult(result);
                            resolveMachine();
                        } else {
                            rejectMachine("Machine data not found");
                        }
                    });
                });

                let issuesFound = false;
                let servicesFound = false;
                const issuesQuery = new Promise<void>((resolveIssues) => {
                    const { query, params } = this.qb.select('Issue', [], innerWhere, issuesOrderBy);
                    tx.executeSql(query, params, (_tx, result) => {
                        if (result && result.rows.length > 0) {
                            issuesFound = true;
                            for (let i = 0; i < result.rows.length; i++) {
                                const issueTable = result.rows.item(i);
                                const issue = JSON.parse(issueTable.data);
                                issues.push({ ...issue, id: issueTable.id });
                                const issueType = Number(issue.type);
                                const issueStatus = Number(issue.status);
                                if ([IssueTypes.MINOR, IssueTypes.CRITICAL, IssueTypes.MORECRITICAL].includes(issueType) && !(issueStatus === IssueStatuses.COMPLETED || issueStatus === IssueStatuses.CLOSED)) {
                                    issuesStatusCount[issueType] = issuesStatusCount[issueType] ? issuesStatusCount[issueType] + 1 : 1;
                                }
                            }
                        }
                        resolveIssues();
                    }, () => resolveIssues());
                });

                const servicesQuery = new Promise<void>((resolveServices) => {
                    const { query, params } = this.qb.select('Service', [], innerWhere);
                    tx.executeSql(query, params, (_tx, result) => {
                        if (result && result.rows.length > 0) {
                            servicesFound = true;
                            for (let i = 0; i < result.rows.length; i++) {
                                const serviceTable = result.rows.item(i);
                                const service = JSON.parse(serviceTable.data);
                                services.push({ ...service, id: serviceTable.id });
                            }
                        }
                        resolveServices();
                    }, () => resolveServices());
                });

                Promise.all([machineQuery, issuesQuery, servicesQuery]).then(() => {
                    if (Object.keys(issuesStatusCount).length > 0) {
                        issuesCount = Object.keys(issuesStatusCount)
                            .map(Number)
                            .sort((a, b) => b - a)
                            .map((type) => ({
                                type,
                                count: issuesStatusCount[type]
                            }));
                    }

                    if (excavatorData) {
                        const carDetails = JSON.parse(excavatorData.data);
                        resolve({
                            ...carDetails,
                            categoryImage: excavatorData.categoryImage,
                            issues: issuesFound ? issues : (carDetails.issues || []),
                            services: servicesFound ? services : (carDetails.services || []),
                            issuesCount: issuesFound ? issuesCount : (carDetails.issuesCount || []),
                        });
                    } else {
                        reject("Excavator data is missing");
                    }
                }).catch((error) => {
                    reject(error);
                });
            }, (error) => {
                reject(error);
            });
        });
    }

    public updateMachineDetail(machine: Machine): void {
        const db = this.SQLite;
        if (!db || !store.getState().machines.isOfflineAvailable(machine.id)) return;

        const machineRow: MachineTable = {
            id: machine.id,
            data: JSON.stringify(machine),
            categoryImage: machine.categoryImage
        };
        this.downloadMachineImages(machineRow).then((updatedMachineRow) => {
            db.transaction((tx) => {
                const { query: deleteQuery, params: deleteParams } = this.qb.delete('Machine', [{name: 'id', value: machine.id}]);
                tx.executeSql(deleteQuery, deleteParams);
                const { query: insertQuery, params: insertParams } = this.qb.insert('Machine', [updatedMachineRow]);
                tx.executeSql(insertQuery, insertParams);
            });
        });
    }

    public registerOperationTimeOrCount(machine: OperatingTimePayload): Promise<void | undefined> {
        const db = this.SQLite;
        if (!db) return Promise.resolve();

        return new Promise<void>((resolve, reject) => {
            db.transaction((tx) => {
                const where = [{name: 'id', value: machine.machineId}];
                const { query: selectQuery, params: selectParams } = this.qb.select('Machine', [], where);
                tx.executeSql(selectQuery, selectParams, (_tx, result) => {
                    const excavatorData = this.qb.getSingleResult(result);
                    if (excavatorData && excavatorData.data) {
                        const excavatorDetails = JSON.parse(excavatorData.data);
                        if (machine.type === OperatingTypes.OPERATING_COUNT) {
                            excavatorDetails.operatingCount = machine.operatingTime;
                            excavatorDetails.lastOperatingCountDate = String(moment().format("YYYY-MM-DD"));
                            excavatorDetails.operatingCountPendingSync = true;
                        } else {
                            excavatorDetails.operatingTime = machine.operatingTime;
                            excavatorDetails.lastOperatingTimeDate = String(moment().format("YYYY-MM-DD"));
                            excavatorDetails.operatingTimePendingSync = true;
                        }
                        const { query: updateQuery, params: updateParams } = this.qb.update('Machine', { data: JSON.stringify(excavatorDetails) }, where);
                        tx.executeSql(updateQuery, updateParams);
                    }
                });
            }, (e) => reject(e), () => {
                syncQueue.enqueueReplace(
                    [
                        {
                            data: JSON.stringify({
                                machineId: machine.machineId,
                                machineIntern: store.getState().machine.data.intern,
                                operatingTime: machine.operatingTime,
                                type: machine.type,
                                userId: store.getState().user.data.id,
                                updatedAt: moment().format('YYYY-MM-DD HH:mm:ss'),
                            }),
                            userId: Number(store.getState().user.data.id),
                            typeOfData: syncTypes.UPDATE_OPERATING_DATA,
                        },
                    ],
                    (existing, next) =>
                        String(existing.machineId) === String(next.machineId) &&
                        String(existing.type) === String(next.type)
                ).then(() => {
                    notifyPeriodicCheckQueueChanged();
                    resolve();
                }, (e) => reject(e));
            });
        });
    }

    public clearOperatingPendingSync(machineId: string | number, type: number): Promise<void> {
        const db = this.SQLite;
        if (!db) {
            return Promise.resolve();
        }
        const field = Number(type) === OperatingTypes.OPERATING_COUNT
            ? 'operatingCountPendingSync'
            : 'operatingTimePendingSync';
        const where = [{name: 'id', value: machineId}];
        return new Promise((resolve, reject) => {
            db.transaction((tx) => {
                const { query, params } = this.qb.select('Machine', [], where);
                tx.executeSql(query, params, (_tx, result) => {
                    const excavatorData = this.qb.getSingleResult(result);
                    if (!excavatorData?.data) {
                        return;
                    }
                    let excavatorDetails: Machine;
                    try {
                        excavatorDetails = JSON.parse(excavatorData.data);
                    } catch {
                        return;
                    }
                    if (!excavatorDetails[field]) {
                        return;
                    }
                    excavatorDetails[field] = false;
                    const update = this.qb.update('Machine', { data: JSON.stringify(excavatorDetails) }, where);
                    tx.executeSql(update.query, update.params);
                });
            }, (e) => reject(e), () => resolve());
        });
    }

    public updateMachineDashboardDetail(machine: Machine) {
        const db = this.SQLite;
        if (!db || !(store.getState().machines.inFavorite(machine.id) || isLastUsedMachine(machine.id))) {
            return;
        }
        const where = [{name: 'id', value: machine.id}];
        const { query: selectQuery, params: selectParams } = this.qb.select('Machine', [], where);
        db.transaction((tx) => {
            tx.executeSql(selectQuery, selectParams, (_tx, result) => {
                const excavatorData = this.qb.getSingleResult(result);
                if (!excavatorData || !excavatorData.data) {
                    return;
                }
                const excavatorDetails = JSON.parse(excavatorData.data);
                excavatorDetails.idleTimeHistoryData = machine.idleTimeHistoryData ? machine.idleTimeHistoryData : [];
                excavatorDetails.fuelConsumptionHistoryData = machine.fuelConsumptionHistoryData ? machine.fuelConsumptionHistoryData : [];
                excavatorDetails.idleHoursThresholdValues = machine.idleHoursThresholdValues ? machine.idleHoursThresholdValues : excavatorDetails.idleHoursThresholdValues;
                excavatorDetails.inspectionData = machine.inspectionData ? machine.inspectionData : [];
                excavatorDetails.inspectionDataOfLast10Weeks = machine.inspectionDataOfLast10Weeks ? machine.inspectionDataOfLast10Weeks : [];
                const { query: updateQuery, params: updateParams } = this.qb.update('Machine', { data: JSON.stringify(excavatorDetails) }, where);
                tx.executeSql(updateQuery, updateParams);
            });
        });
    }

    public upsertMachines(machines: Machine[]) {
        const db = this.SQLite;
        if (!db || !machines.length) {
            return Promise.resolve();
        }
        return Promise.all(machines.map(machine => this.downloadMachineImages({
            data: JSON.stringify(machine),
            id: machine.id,
            categoryImage: machine.categoryImage,
        }))).then(rows => new Promise((resolve, reject) => {
            db.transaction((tx) => {
                rows.forEach(row => {
                    const remove = this.qb.delete('Machine', [{name: 'id', value: row.id}]);
                    const insert = this.qb.insert('Machine', [row]);
                    tx.executeSql(remove.query, remove.params);
                    tx.executeSql(insert.query, insert.params);
                });
            }, (e) => reject(e), () => resolve(undefined));
        }));
    }

    public upsertIssues(issues: Array<Issue & { machineId?: string | number }> = []) {
        const db = this.SQLite;
        if (!db || !issues.length) {
            return Promise.resolve();
        }
        const validIssues = issues.filter(issue => issue && issue.id !== undefined && issue.id !== null);
        if (!validIssues.length) {
            return Promise.resolve();
        }
        const ids = validIssues.map(i => i.id);
        const { query: selectQuery, params: selectParams } = this.qb.selectIn('Issue', 'id', ids);

        return new Promise((resolve, reject) => {
            db.executeSql(selectQuery, selectParams, (resultSet) => {
                const existingRows = this.qb.getAllResult(resultSet) || [];
                const existingMap = new Map<string, any>();
                existingRows.forEach(row => {
                    existingMap.set(String(row.id), row);
                });

                db.transaction((tx) => {
                    validIssues.forEach(issue => {
                        const existing = existingMap.get(String(issue.id));
                        let mergedData: any = { ...issue };
                        let images = '';
                        if (existing) {
                            try {
                                const parsedExistingData = JSON.parse(existing.data || '{}');
                                mergedData = {
                                    ...parsedExistingData,
                                    ...issue,
                                    categoryId: (issue as any).categoryId ?? parsedExistingData.categoryId,
                                    issueDepartmentId: (issue as any).issueDepartmentId ?? parsedExistingData.issueDepartmentId,
                                    departmentId: (issue as any).departmentId ?? parsedExistingData.departmentId,
                                    sortingType: (issue as any).sortingType ?? parsedExistingData.sortingType,
                                    files: parsedExistingData.files ?? (issue as any).files,
                                    chat: parsedExistingData.chat ?? (issue as any).chat,
                                    chatUsers: parsedExistingData.chatUsers ?? (issue as any).chatUsers,
                                    workDetails: parsedExistingData.workDetails ?? (issue as any).workDetails,
                                    issueCreatedUserId: parsedExistingData.issueCreatedUserId ?? (issue as any).issueCreatedUserId,
                                };
                            } catch (e) {
                                console.error('Failed to parse existing issue data', e);
                            }
                            images = existing.images || '';
                        }
                        const row = {
                            id: issue.id,
                            machineId: issue.machineId ?? existing?.machineId,
                            images: images,
                            data: JSON.stringify({...mergedData, images: ''}),
                        };
                        const remove = this.qb.delete('Issue', [{name: 'id', value: issue.id}]);
                        const insert = this.qb.insert('Issue', [row]);
                        tx.executeSql(remove.query, remove.params);
                        tx.executeSql(insert.query, insert.params);
                    });
                }, (e) => reject(e), () => resolve(undefined));
            }, (e) => reject(e));
        });
    }

    public upsertServices(services: Array<Service & { machineId?: string | number }> = []) {
        const db = this.SQLite;
        if (!db || !services.length) {
            return Promise.resolve();
        }
        return new Promise((resolve, reject) => {
            db.transaction((tx) => {
                services.forEach(service => {
                    if (!service || service.id === undefined || service.id === null) {
                        return;
                    }
                    const row = {
                        id: service.id,
                        machineId: service.machineId,
                        data: JSON.stringify(service),
                    };
                    const remove = this.qb.delete('Service', [{name: 'id', value: service.id}]);
                    const insert = this.qb.insert('Service', [row]);
                    tx.executeSql(remove.query, remove.params);
                    tx.executeSql(insert.query, insert.params);
                });
            }, (e) => reject(e), () => resolve(undefined));
        });
    }

    public async evictMachine(id: string | number) {
        const db = this.SQLite;
        if (!db) {
            return Promise.resolve();
        }
        const hasPending = await hasPendingSyncForMachine(id);
        if (hasPending) {
            return Promise.resolve();
        }
        const fileTransfer = new FileTransfer();
        return new Promise((resolve, reject) => {
            db.transaction((tx) => {
                const { query, params } = this.qb.select('Machine');
                tx.executeSql(query, params, (_tx, result) => {
                    const rows = this.qb.getAllResult(result);
                    const row = rows.find(item => String(item.id) === String(id));
                    let intern = '';
                    if (row && row.data) {
                        try {
                            intern = JSON.parse(row.data).intern || '';
                        } catch (e) {
                            intern = '';
                        }
                    }
                    const internStillUsed = !!intern && rows.some(item => {
                        if (String(item.id) === String(id) || !item.data) {
                            return false;
                        }
                        try {
                            return JSON.parse(item.data).intern === intern;
                        } catch (e) {
                            return false;
                        }
                    });
                    const deleteMachine = this.qb.delete('Machine', [{name: 'id', value: id}]);
                    const deleteIssues = this.qb.delete('Issue', [{name: 'machineId', value: id}]);
                    const deleteServices = this.qb.delete('Service', [{name: 'machineId', value: id}]);
                    const deleteDocuments = this.qb.delete('Documents', [{name: 'machineId', value: id}]);
                    const deleteSpec = this.qb.delete('MachineSpecification', [{name: 'id', value: id}]);
                    tx.executeSql(deleteMachine.query, deleteMachine.params);
                    tx.executeSql(deleteIssues.query, deleteIssues.params);
                    tx.executeSql(deleteServices.query, deleteServices.params);
                    tx.executeSql(deleteDocuments.query, deleteDocuments.params);
                    tx.executeSql(deleteSpec.query, deleteSpec.params);
                    if (intern && !internStillUsed) {
                        tx.executeSql('DELETE FROM MachineSpecification WHERE hovedNo = ?', [intern]);
                    }
                });
            }, (e) => reject(e), () => {
                fileTransfer.removeFolder(`issueImages/${id}`);
                fileTransfer.removeFolder(`archives/${id}`);
                resolve(undefined);
            });
        });
    }
}
